import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import {
  APP_VERSION_CHECK_INTERVAL_MS,
  fetchAppVersion,
  isBroadcastOutputPath,
  type AppVersionFetcher,
  type AppVersionManifest,
} from "@/appVersion";

interface AppVersionContextValue {
  deployedManifest: AppVersionManifest | null;
  disabled: boolean;
}

const AppVersionContext = createContext<AppVersionContextValue>({
  deployedManifest: null,
  disabled: true,
});

interface AppVersionProviderProps {
  children: ReactNode;
  checkIntervalMs?: number;
  enabled?: boolean;
  fetchVersion?: AppVersionFetcher;
}

/**
 * One app-wide version monitor shared by the update notice and deployment-
 * gated controls. Public broadcast routes deliberately never poll.
 */
export function AppVersionProvider({
  children,
  checkIntervalMs = APP_VERSION_CHECK_INTERVAL_MS,
  enabled = import.meta.env.PROD,
  fetchVersion = fetchAppVersion,
}: AppVersionProviderProps) {
  const { pathname } = useLocation();
  const [deployedManifest, setDeployedManifest] = useState<AppVersionManifest | null>(null);
  const disabled = !enabled || isBroadcastOutputPath(pathname);

  useEffect(() => {
    if (disabled) {
      setDeployedManifest(null);
      return;
    }

    const controller = new AbortController();
    let active = true;
    let checking = false;

    const checkForUpdate = async () => {
      if (checking) return;
      checking = true;
      try {
        const manifest = await fetchVersion(controller.signal);
        if (active && manifest) setDeployedManifest(manifest);
      } finally {
        checking = false;
      }
    };

    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") void checkForUpdate();
    };

    void checkForUpdate();
    const interval = window.setInterval(checkWhenVisible, checkIntervalMs);
    window.addEventListener("focus", checkForUpdate);
    window.addEventListener("online", checkForUpdate);
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      active = false;
      controller.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", checkForUpdate);
      window.removeEventListener("online", checkForUpdate);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, [checkIntervalMs, disabled, fetchVersion]);

  return (
    <AppVersionContext.Provider value={{ deployedManifest, disabled }}>
      {children}
    </AppVersionContext.Provider>
  );
}

export function useAppVersion(): AppVersionContextValue {
  return useContext(AppVersionContext);
}
